'use client';

import { useEffect, useEffectEvent } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import { hasGraphQLErrors, isSurveyExpiredError } from '@/shared-utilities';
import {
  GET_POLL_RESULT,
  GetProjectQuery,
  V2_GET_INSIGHT,
} from '@/survey-graphql';
import { toast } from '@/ui';

import { NIL_UUID } from './project-response.consts';
import { shouldSkipPollResults } from './project-response.helpers';

export function useProjectResponseQueries(
  project: GetProjectQuery['project'],
  insightId: string | null,
  isPublished: boolean,
  onProjectUnpublished: () => void,
  enableLegacyPollResults = true
) {
  const {
    data: pollResultData,
    loading: isPollResultLoading,
    error: pollResultError,
    stopPolling: stopPollResultPolling,
  } = useQuery(GET_POLL_RESULT, {
    variables: {
      id: project.id,
    },
    skip:
      !enableLegacyPollResults ||
      shouldSkipPollResults(project.isPoll, isPublished) ||
      !insightId ||
      insightId === NIL_UUID,
    pollInterval: 5000,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
  });

  const {
    data: insightData,
    loading: insightLoading,
    error: insightError,
  } = useQuery(V2_GET_INSIGHT, {
    skip: !insightId,
    variables: { id: insightId ?? '' },
    fetchPolicy: insightId === NIL_UUID ? 'cache-only' : 'cache-first',
  });

  const onInsightError = useEffectEvent((apolloError: ErrorLike) => {
    toast.add({ title: apolloError.message, type: 'error' });
  });

  const onPollResultError = useEffectEvent((apolloError: ErrorLike) => {
    if (hasGraphQLErrors(apolloError)) stopPollResultPolling();
    if (isSurveyExpiredError(apolloError)) onProjectUnpublished();
  });

  useEffect(() => {
    if (insightError) onInsightError(insightError);
  }, [insightError]);

  useEffect(() => {
    if (pollResultError) onPollResultError(pollResultError);
  }, [pollResultError]);

  return {
    pollResultData,
    insightData,
    isPollResultLoading,
    insightLoading,
  };
}
